import tenantApi from '@api';
import tenantData from '@data';
import tenantUtils from '@utils';
import tenantTheme from '@theme';
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Group, Icon, Label, SelectSearch, Spinner, Tag } from '../../../../../components/common';
import { IconStyled } from '../../../../../components/common/icon/IconStyled';
import Algolia from '../../../../../services/algolia';
import { GoogleMapModal } from '../../../../../components/post-listing/google-map-modal/google-map-modal';
import { changeLocationClickEvent, changeLocationConfirmEvent } from '../../../../../services/analyticsService';
import { sortLocationsByRank } from '../../../../../utility/utility';
import tenantConstants from '@constants';
import GoogleMapComponent from '../../../../../components/post-listing/google-map/google-map';
import { Col, Row } from 'antd';

const USE_INLINE_MAP = !!tenantConstants.FORM;

// Teal home pin to match the Figma map marker (badge + pin drop, with soft shadow).
const TEAL_MARKER_ICON = `data:image/svg+xml,${encodeURIComponent(
  `<svg width="48" height="61" viewBox="0 0 48 61" fill="none" xmlns="http://www.w3.org/2000/svg"><g filter="url(#filter0_d_51383_1226)"><rect x="10" y="8" width="28" height="28" rx="14" fill="#006169"/><g clip-path="url(#clip0_51383_1226)"><path d="M29.6756 21.2186L29.6765 21.2194C30.1064 21.6503 30.1062 22.3506 29.6759 22.7812C29.4671 22.99 29.1897 23.1049 28.8947 23.1049H28.7138V26.7072V28H25.1541V27.6484V24.8242C25.1541 24.4989 24.8895 24.2343 24.5643 24.2343H23.4347C23.1094 24.2343 22.8448 24.4989 22.8448 24.8242V27.6484V28H19.2851V26.7072V23.1049H19.09C19.0786 23.1049 19.0672 23.1043 19.056 23.1032C18.7787 23.0913 18.5193 22.9775 18.3229 22.781C17.8931 22.3512 17.8923 21.652 18.3208 21.2211L18.3258 21.216L23.2182 16.3236C23.427 16.1149 23.7044 16 23.9995 16C24.2945 16 24.5719 16.115 24.7806 16.3237L29.6756 21.2186Z" fill="white"/></g><path d="M23.8301 49C23.8301 49 24.3317 42.9495 26.8301 39.1818C29.0544 35.8273 34.6459 31 34.6459 31H13.3301C13.3301 31 18.6773 35.9244 20.8301 39.1818C23.3399 42.9794 23.8301 49 23.8301 49Z" fill="#006169"/></g><defs><filter id="filter0_d_51383_1226" x="0" y="0" width="48" height="61" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/><feOffset dy="2"/><feGaussianBlur stdDeviation="5"/><feComposite in2="hardAlpha" operator="out"/><feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/><feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_51383_1226"/><feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_51383_1226" result="shape"/></filter><clipPath id="clip0_51383_1226"><rect width="12" height="12" fill="white" transform="translate(18 16)"/></clipPath></defs></svg>`,
)}`;

const LocationSelect = (props) => {
  const {
    value,
    error,
    touched,
    setFieldValue,
    setFieldTouched,
    item,
    onCityChange = () => {},
    onPlotSelect,
    showPlot,
    disabled,
    forUpdate,
    crossCity,
    renderCrossCityAlerts = () => null,
    onLocationSelect,
    name,
    hideLocation,
    hidePlot,
    hideCrossCity,
    prefixIcon,
    labelIcon,
    showPrefixIcon = true,
    showLableIcon = true,
    user,
    formik,
    propertyType,
    mode,
    label,
    placeholder,
    lockCityAfterSelection = true,
  } = props;
  const locationLocked = disabled === true;
  const [cities, setCities] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [isPlotFinderExists, setShowPlotFinder] = useState(false);
  const [mapEditMode, setMapEditMode] = useState(false);
  const [mapSheetOpen, setMapSheetOpen] = useState(false);
  const [draftMapPin, setDraftMapPin] = useState(null);
  const isMobile = useSelector((state) => state.app.AppConfig.isMobile);
  const { t } = useTranslation();

  const plotFinderRef = useRef();
  const locationRef = useRef();
  const modalRef = useRef();

  const locationNotEditable = formik?.values?.is_location_editable === false;
  const showLocationReadOnlyTag = forUpdate && locationNotEditable;

  const regaDistrict = formik?.values?.rega_details?.location?.district || null;

  useEffect(() => {
    if (value?.city?.name) {
      fetchSelectedCity(value?.city?.name);
    }
  }, [value?.city?.name]);

  // District options must follow the city chosen in this field (value.city), not formik.values.location
  // (listing root from initialValues), which would pin the same districts for every city.
  useEffect(() => {
    const city = value?.city;
    const cityId = city?.location_id ?? city?.id;
    if (!cityId || !city) {
      setLocations([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const res = await fetchChildLocations(city);
      if (cancelled) return;
      setLocations(
        res?.hits?.length
          ? res.hits.map((e) => ({
              ...e,
              title: tenantUtils.getLocalisedString(e, 'title'),
              value: e?.location_id,
            }))
          : [],
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [value?.city?.location_id, value?.city?.id]);

  useEffect(() => {
    fetchCities()
  }, []);

  const fetchChildLocations = async (location, withParent = false, callbackSuccess = () => {}, callback = () => {}) => {
    if (!!location) {
      try {
        const id = location?.id || location?.location_id;
        setLocationsLoading(true);
        const queryObject = withParent
          ? Algolia.getLocationsChildsFromLevelWithParent(location.id, location.level)
          : Algolia.getLocationsChildsFromLevel(id, location.level);
        const response = await Algolia.getLocationsIndex().search('', queryObject);
        setLocationsLoading(false);
        if (response) {
          callbackSuccess(response);
          return response;
        } else {
          callback();
        }
      } catch (e) {
        callback();
      }
    } else {
      callback();
      return;
    }
  };

  const fetchSelectedCity = async (name) => {
    setCitiesLoading(true);
    try {
      const queryObject = Algolia.getCitiesByName(name);
      const response = await Algolia.getLocationsIndex().search('', queryObject);
      if (response) {
        setCities([
          ...(response?.hits?.length
            ? response?.hits?.map((e) => ({
                ...e,
                city_title: tenantUtils.getLocalisedString(e, 'title'),
              }))
            : []),
        ]);
      }
    } catch (error) {
      console.error('Failed to fetch city:', error?.message);
    } finally {
      setCitiesLoading(false);
    }
  };
  const fetchCitiesOnSearch = async (text, callbackSuccess = () => {}, callback) => {
    try {
      let response = null;
      if (!!text) {
        const queryObject = Algolia.getCitiesByName(text);
        response = await Algolia.getLocationsIndex().search('', queryObject);
      } else {
        const queryObject = Algolia.getAllCities();
        response = await Algolia.getLocationsIndex().search('', queryObject);
      }

      if (response) {
        callbackSuccess({ ...response, hits: sortLocationsByRank(response.hits) });
        return response;
      } else {
        callback();
      }
    } catch (e) {
      callback();
    }
  };

  const fetchCities = async () => {
    setCitiesLoading(true);
    try {
      const queryObject = Algolia.getAllCities();
      const response = await Algolia.getLocationsIndex().search('', queryObject);
      if (response) {
        setCities(
          response?.hits?.length
            ? sortLocationsByRank(response.hits).map((e) => ({
                ...e,
                city_title: tenantUtils.getLocalisedString(e, 'title'),
              }))
            : [],
        );
        response.hits;
      }
    } catch (error) {
      console.error('Failed to fetch cities:', error?.message);
    } finally {
      setCitiesLoading(false);
    }
  };

  const fetchLocations = async (text, callbackSuccess = () => {}, callback) => {
    if (!!text) {
      try {
        const queryObject = Algolia.getLocationsByName(value?.city?.location_id ?? value?.city?.id, text);
        const response = await Algolia.getLocationsIndex().search('', queryObject);
        if (response) {
          callbackSuccess(response);
          return response;
        } else {
          callback();
        }
      } catch (e) {
        callback();
      }
    } else {
      callback();
      return;
    }
  };

  const onChangeCity = (cityValue, option) => {
    if (!option) {
      setShowPlotFinder(false);
      plotFinderRef.current && plotFinderRef.current.clearOptions();
      locationRef.current && locationRef.current.clearOptions();
      setFieldValue(item.key, { city: null, location: null, map: null }, true);
      return;
    }
    let locationOBJ = {};
    if (value?.city?.location_id != option.location_id) {
      plotFinderRef.current && plotFinderRef.current.clearOptions();
      locationRef.current && locationRef.current.clearOptions();
      onCityChange(option);
      locationOBJ['city'] = { ...option, id: option?.city_id };
    } else {
      locationOBJ = value;
    }
    setFieldValue(
      item.key,
      {
        ...locationOBJ,
        map: { longitude: option.longitude, latitude: option.latitude, type: 'city' },
      },
      true,
    );
  };

  const onChangeLocation = (locationValue, option) => {
    if (!option) {
      setShowPlotFinder(false);
      plotFinderRef.current && plotFinderRef.current.clearOptions();
      setFieldValue(
        item.key,
        {
          city: value?.city,
          location: null,
          map:
            value?.city?.longitude != null && value?.city?.latitude != null
              ? { longitude: value.city.longitude, latitude: value.city.latitude, type: 'city' }
              : value?.map ?? null,
        },
        true,
      );
      onLocationSelect(null);
      return;
    }
    if (option.location_id != value.location?.location_id) {
      let locationAdd = {};
      locationAdd['location'] = { ...option, id: option?.location_id };
      setFieldValue(item.key, {
        city: value?.city,
        ...locationAdd,
        map: { longitude: option.longitude, latitude: option.latitude, type: 'location' },
      });
      plotFinderRef.current && plotFinderRef.current.clearOptions();
      onLocationSelect(option);
    }
  };

  const onChangePlotNumber = (val, option) => {
    if (option) {
      setFieldValue(item.key, {
        ...value,
        plot: option,
        map: {
          longitude: option.geometry.coordinates[0],
          latitude: option.geometry.coordinates[1],
          type: 'plotfinder',
        },
      });
      onPlotSelect(option);
    } else {
      setFieldValue(item.key, {
        ...value,
        plot: null,
        map: {
          longitude: Number(value?.location.longitude),
          latitude: Number(value?.location.latitude),
          type: 'location',
        },
      });
      onPlotSelect(null);
    }
  };

  const onConfirmMapLocation = ({ latitude, longitude }, val = value) => {
    setFieldValue(item.key, { ...val, map: { longitude, latitude, type: 'map' } }, true);
  };

  const onCancelMapModal = () => {
    const longitude = value?.map?.longitude || value.location?.longitude;
    const latitude = value?.map?.latitude || value.location?.latitude;
    setFieldValue(item.key, { ...value, map: { longitude, latitude, type: 'map' } }, true);
  };

  const mapInteractionDisabled = locationLocked || disabled?.location || locationNotEditable;

  const seedDraftFromCurrent = () => {
    const pos = getMapPosition();
    setDraftMapPin(
      pos?.latitude != null && pos?.longitude != null
        ? { latitude: pos.latitude, longitude: pos.longitude }
        : null,
    );
  };

  // Desktop: edit inline. Mobile: open the bottom sheet.
  const enterMapEditMode = () => {
    seedDraftFromCurrent();
    changeLocationClickEvent(user, formik?.values, !forUpdate);
    if (isMobile) setMapSheetOpen(true);
    else setMapEditMode(true);
  };

  const commitMapPin = () => {
    if (draftMapPin) {
      onConfirmMapLocation(draftMapPin);
      changeLocationConfirmEvent(user, formik?.values, !forUpdate);
    }
  };

  const handleInlineMapSave = () => {
    commitMapPin();
    setMapEditMode(false);
    setDraftMapPin(null);
  };

  const handleSheetSave = () => {
    commitMapPin();
    setMapSheetOpen(false);
    setDraftMapPin(null);
  };

  const handleSheetCancel = () => {
    setMapSheetOpen(false);
    setDraftMapPin(null);
  };

  const getMapPosition = useCallback(() => {
    const position = value?.map || value?.location;
    return {
      lat: position?.latitude,
      lng: position?.longitude,
      latitude: position?.latitude,
      longitude: position?.longitude,
    };
  }, [value?.map, value?.location]);

  const cityOptions = useMemo(() => {
    if (!value?.city) return cities;
    const list = cities ?? [];
    const selectedId = value.city.location_id ?? value.city.id;
    if (list.some((c) => (c?.location_id ?? c?.id) === selectedId)) return cities;
    const title = value.city.title ?? { en: String(value.city.name ?? selectedId) };
    return [{ ...value.city, location_id: selectedId, id: selectedId, title }, ...list];
  }, [cities, value?.city]);

  /** When editing, Algolia district hits may not include the saved id yet — seed the current row so the label shows. */
  const districtOptions = useMemo(() => {
    const loc = value?.location;
    const list = locations ?? [];
    if (!loc) return list;
    const selectedId = loc.location_id ?? loc.id;
    if (selectedId == null || selectedId === '') return list;
    const idKey = (x) => x?.location_id ?? x?.value ?? x?.id;
    if (list.some((x) => idKey(x) === selectedId)) return list;
    const title = loc.title ?? { en: String(loc.name ?? selectedId) };
    return [{ ...loc, location_id: selectedId, id: selectedId, value: selectedId, title }, ...list];
  }, [locations, value?.location]);

  const renderLocationTag = () => {
    const locationChip = [value?.plot?.plot_number && `Plot # ${value?.plot?.plot_number}`, value.location, value.city]
      .filter((e) => !!e)
      .map((e) => e?.title?.en || e?.title || e)
      .join(', ');
    return (
      <Group template="max-content auto" gap="16px">
        <IconStyled>
          <Icon icon="MdOutlineMap" />
        </IconStyled>
        <Group template="initial" gap="8px">
          <Label>{t('Location')}</Label>
          <div>
            <Tag shape="round" bordered disabled>
              {value.location && locationChip}
            </Tag>
          </div>
        </Group>
      </Group>
    );
  };

  return (
    <Group gap="32px" template="minmax(0, 1fr)">
      {/* District provided by REGA → hide the City / Location-District selects (the district
          is shown in the "Listing Details from REGA" card). The map below still renders and
          auto-zooms into the district. When REGA has no district, show the selects as usual. */}
      {regaDistrict ? null : showLocationReadOnlyTag ? (
        renderLocationTag()
      ) : (
        <>
          <Group gap={hideLocation ? '8px' : '0'}>
            <SelectSearch
              name={item.key}
              onChange={onChangeCity}
              onBlur={() => setFieldTouched(item.key, { city: true })}
              label={label ? t(label) : t(tenantConstants.LISTING_LOCATIONS.label)}
              placeholder={placeholder ? t(placeholder) : t(`Select ${tenantConstants.LISTING_LOCATIONS.label}`)}
              fetchApi={fetchCitiesOnSearch}
              payloadKey="hits"
              filterOption={false}
              value={value?.city?.location_id}
              prefixIcon={!!showPrefixIcon && 'MdCircle'}
              labelIcon={!!showLableIcon && 'MdOutlinePlace'}
              initialOptions={cityOptions}
              getOptionValue={(e) => e.location_id}
              getOptionLabel={(e) => tenantUtils.getLocalisedString(e, 'title')}
              loading={citiesLoading}
              allowClear
              valueAsObj
              errorMsg={
                (error?.city && touched?.city && error?.city) ||
                (!!touched && Object.keys(touched).length === 0 && error?.city) ||
                null
              }
              disabled={
                locationLocked ||
                disabled?.city ||
                (lockCityAfterSelection && !!(value?.city?.location_id ?? value?.city?.id))
              }
              mode={mode}
            />
            {!hideCrossCity && (
              <div style={{ marginInlineStart: 52, marginBlockStart: 8 }}>{renderCrossCityAlerts()}</div>
            )}
          </Group>
          {!hideLocation && (
            <div>
              <SelectSearch
                key={value?.city?.location_id ?? value?.city?.id ?? 'no-city'}
                fetchApi={fetchLocations}
                payloadKey="hits"
                disabled={
                  locationLocked || !(value?.city?.location_id ?? value?.city?.id) || locationNotEditable || !!regaDistrict
                }
                valueAsObj
                getOptionValue={(e) => e.location_id}
                getOptionLabel={(e) => tenantUtils.getLocalisedString(e, 'title')}
                filterOption={false}
                value={value?.location?.location_id}
                onChange={onChangeLocation}
                onBlur={() => setFieldTouched(item.key, { city: true, location: true })}
                initialOptions={districtOptions}
                label={t('Location/District')}
                labelIcon={USE_INLINE_MAP ? 'MdOutlinePlace' : 'MdOutlineMap'}
                placeholder={
                  !(value?.city?.location_id ?? value?.city?.id)
                    ? t('Search Location')
                    : t('Search from ') + tenantUtils.getLocalisedString(value?.city, 'title') ||
                      tenantUtils.getLocalisedString(value?.city, 'name')
                }
                prefixIcon="location"
                errorMsg={
                  (error?.location && touched?.location && error?.location) ||
                  (!!touched && Object.keys(touched).length === 0 && error?.location) ||
                  null
                }
                ref={locationRef}
              />
            </div>
          )}
        </>
      )}

      {showPlot && USE_INLINE_MAP && (
        <Suspense fallback={<Spinner />}>
          <Group template="max-content auto" gap="16px">
            <IconStyled>
              <Icon icon="MdOutlineMap" />
            </IconStyled>
            <Group template="initial" gap="8px">
              <Label>{t('Map Pin')}</Label>

                {/* INLINE MAP. Desktop edits inline; mobile opens a bottom sheet. */}
                <div style={{ position: 'relative', borderRadius: 8, overflow: 'hidden' }}>
                  <GoogleMapComponent
                    onCallback={
                      !isMobile && mapEditMode
                        ? ({ lat, lng }) => setDraftMapPin({ latitude: lat, longitude: lng })
                        : undefined
                    }
                    onClick={() => {
                      if (mapInteractionDisabled) return;
                      if (isMobile) enterMapEditMode();
                      else if (!mapEditMode) enterMapEditMode();
                    }}
                    disabled={mapInteractionDisabled}
                    readOnly={isMobile ? true : !mapEditMode}
                    enableInteractions={isMobile ? false : mapEditMode}
                    lat={!isMobile && mapEditMode && draftMapPin ? draftMapPin.latitude : getMapPosition()?.lat}
                    lng={!isMobile && mapEditMode && draftMapPin ? draftMapPin.longitude : getMapPosition()?.lng}
                    zoom={regaDistrict ? 15 : 13}
                    zoomControlPosition="LEFT_TOP"
                    markerIcon={TEAL_MARKER_ICON}
                    mapStyle={{ width: '100%', height: isMobile ? '120px' : '260px', borderRadius: '8px', overflow: 'hidden' }}
                    className="location-select-map"
                  />

                  {!mapInteractionDisabled && (
                    <>
                      {/* "Pin moved to {coords}" white pill directly below the pin (after a move) */}
                      {!mapEditMode &&
                        value?.map?.type === 'map' &&
                        getMapPosition()?.lat != null &&
                        getMapPosition()?.lng != null && (
                          <div
                            style={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: `translate(-50%, ${isMobile ? 16 : 22}px)`,
                              zIndex: 5,
                              background: '#006169',
                              color: '#fff',
                              fontSize: isMobile ? 9 : 12,
                              fontWeight: 600,
                              padding: isMobile ? '3px 8px' : '6px 12px',
                              borderRadius: 18,
                              whiteSpace: 'nowrap',
                              boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                            }}
                          >
                            {t('Pin moved to')} {Number(getMapPosition().lat).toFixed(7)},{' '}
                            {Number(getMapPosition().lng).toFixed(7)}
                          </div>
                        )}

                      {/* Bottom-right white action button: Adjust (view) / Save (desktop edit) */}
                      <button
                        type="button"
                        onClick={!isMobile && mapEditMode ? handleInlineMapSave : enterMapEditMode}
                        style={{
                          position: 'absolute',
                          bottom: isMobile ? 8 : 12,
                          insetInlineEnd: isMobile ? 8 : 12,
                          zIndex: 5,
                          background: '#fff',
                          color: '#006169',
                          border: '1px solid #006169',
                          borderRadius: isMobile ? 6 : 8,
                          padding: isMobile ? '4px 12px' : '8px 22px',
                          fontSize: isMobile ? 12 : 14,
                          fontWeight: 600,
                          cursor: 'pointer',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        }}
                      >
                        {!isMobile && mapEditMode ? t('Save') : t('Adjust')}
                      </button>
                    </>
                  )}
                </div>

                {/* MOBILE: bottom-sheet map editor */}
                {isMobile && mapSheetOpen && (
                  <div
                    style={{
                      position: 'fixed',
                      inset: 0,
                      background: 'rgba(0,0,0,0.5)',
                      zIndex: 1100,
                      display: 'flex',
                      alignItems: 'flex-end',
                    }}
                    onClick={handleSheetCancel}
                  >
                    <div
                      style={{
                        background: '#fff',
                        borderRadius: '16px 16px 0 0',
                        width: '100%',
                        maxHeight: '88vh',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '16px',
                        }}
                      >
                        <span style={{ color: '#222', fontSize: 16, fontWeight: 700 }}>
                          {t('Drag to Select Location on Map')}
                        </span>
                        <button
                          type="button"
                          aria-label={t('Close')}
                          onClick={handleSheetCancel}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', lineHeight: 0, padding: 4 }}
                        >
                          <Icon icon="MdClose" size={20} color="#9D9D9D" />
                        </button>
                      </div>

                      <div style={{ padding: '0 16px' }}>
                        <GoogleMapComponent
                          onCallback={({ lat, lng }) => setDraftMapPin({ latitude: lat, longitude: lng })}
                          readOnly={false}
                          enableInteractions={true}
                          lat={draftMapPin ? draftMapPin.latitude : getMapPosition()?.lat}
                          lng={draftMapPin ? draftMapPin.longitude : getMapPosition()?.lng}
                          zoom={regaDistrict ? 15 : 13}
                          zoomControlPosition="LEFT_TOP"
                          markerIcon={TEAL_MARKER_ICON}
                          mapStyle={{ width: '100%', height: '52vh', borderRadius: '12px', overflow: 'hidden' }}
                          className="location-select-map-sheet"
                        />
                      </div>

                      <div style={{ display: 'flex', gap: 12, padding: 16 }}>
                        <button
                          type="button"
                          onClick={handleSheetCancel}
                          style={{
                            flex: 1,
                            background: '#fff',
                            color: '#4C4A4A',
                            border: '1px solid #F0F0F0',
                            borderRadius: 6,
                            padding: '12px 0',
                            fontSize: 14,
                            fontWeight: 700,
                            cursor: 'pointer',
                          }}
                        >
                          {t('Cancel')}
                        </button>
                        <button
                          type="button"
                          onClick={handleSheetSave}
                          style={{
                            flex: 2,
                            background: tenantTheme['primary-color'],
                            color: '#fff',
                            border: 'none',
                            borderRadius: 6,
                            padding: '12px 0',
                            fontSize: 14,
                            fontWeight: 700,
                            cursor: 'pointer',
                          }}
                        >
                          {t('Save Changes')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
            </Group>
          </Group>
        </Suspense>
      )}

      {showPlot && !USE_INLINE_MAP && (
        <>
          <Suspense fallback={<Spinner />}>
            <Row>
              <Col xs={22} lg={22} xl={22} offset={2}>
                <GoogleMapComponent
                  onClick={() => {
                    modalRef && modalRef.current.showModal();
                    changeLocationClickEvent(user, formik?.values, !forUpdate);
                  }}
                  disabled={locationLocked || disabled?.location || locationNotEditable}
                  readOnly={true}
                  enableInteractions={true}
                  lat={getMapPosition()?.lat}
                  lng={getMapPosition()?.lng}
                  zoom={13}
                  mapStyle={{ width: '100%', height: '200px', borderRadius: '8px', overflow: 'hidden' }}
                  className="location-select-map"
                />
              </Col>
            </Row>
          </Suspense>
          <GoogleMapModal
            modalRef={modalRef}
            value={value}
            forUpdate={forUpdate}
            onCancelMapModal={onCancelMapModal}
            onConfirmMapLocation={onConfirmMapLocation}
            getMapPosition={getMapPosition}
            user={user}
            formikValues={formik?.values}
            propertyType={propertyType}
          />
        </>
      )}
    </Group>
  );
};
export default LocationSelect;
